<template>
  <Pane
    :value="modelValue"
    :tabs="tabs"
    :current-tab="currentTab"
    temporary
    @update:modelValue="closePane"
  >
    <template #content-share>
      <v-row>
        <v-col>
          <h3>
            {{ $t('project.question-answer.QuestionAnswerItemSharePane.shareQuestion') }}
          </h3>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <app-radio-group
            :active-option="shareType"
            :options="shareTypeOptions"
            @change-active-option="onShareTypeChange"
          />
        </v-col>
      </v-row>
      <v-row v-if="shareType === SHARE_TYPE_OPTIONS.CUSTOM" class="mt-0">
        <v-col>
          <DocumentSharePicker :force-pm-selection="true" :force-current-user-selection="true"/>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <VisualTextEditor
            v-model:value="customMessage"
            ref="customMessageEditor"
            qa-variant
            :placeholder="$t('project.question-answer.QuestionAnswerItemSharePane.customMessagePlaceholder')"
          />
          <div class="text-right mt-1">
            <TextCounter :current-length="notifyMessageLength" :max-length="NOTIFY_MESSAGE_MAX_LENGTH" />
          </div>
          <span v-if="notifyMessageErrors.length > 0" class="text-error text-caption">
            {{ notifyMessageErrors[0] }}
          </span>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <div class="d-flex justify-end ga-4">
            <app-button
              type="outlined"
              variant="neutral"
              :disabled="patchQuestionRightsPending"
              @click="closePane()"
            >
              {{ $t('common.cancel') }}
            </app-button>
            <app-button
              :disabled="shareDisabled"
              data-locator="update-rights-button"
              :loading="patchQuestionRightsPending"
              @click="preparePatchQuestionRights"
            >
              {{ $t('project.question-answer.QuestionAnswerItemSharePane.update') }}
            </app-button>
          </div>
        </v-col>
      </v-row>
    </template>
  </Pane>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import DocumentSharePicker from '@/common/document-share/DocumentSharePicker.vue'
import Pane from '@/common/Pane.vue'
import TextCounter from '@/common/TextCounter.vue'
import { SHARE_TYPE_OPTIONS } from '@/common/utils/shareTypes'
import { escapeHtml } from '@/common/utils/strings'
import { toast } from '@/common/utils/toast'
import VisualTextEditor from '@/common/visual-text-editor/VisualTextEditor.vue'
import { QUESTION_ANSWER_LIST_ROUTE_NAME } from '@/router/routerData'
import { DocumentSharePickerActionTypes } from '@/store/modules/document-share-picker/action_types'
import { QuestionAnswerActionTypes } from '@/store/modules/question-answer/action_types'

const NOTIFY_MESSAGE_MAX_LENGTH = 3000

export default {
  name: 'QuestionAnswerItemSharePane',
  components: {
    DocumentSharePicker,
    Pane,
    TextCounter,
    VisualTextEditor,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
    modelValue: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['update:modelValue'],
  data () {
    return {
      NOTIFY_MESSAGE_MAX_LENGTH,
      tabs: [{ title: this.$t('project.question-answer.QuestionAnswerItemSharePane.tabTitle'), id: 'share' }],
      currentTab: 'share',
      SHARE_TYPE_OPTIONS,
      shareTypeValue: '',
      customMessage: '',
    }
  },
  computed: {
    ...mapState('questionAnswer', ['question', 'patchQuestionRightsPending']),
    ...mapState('documentSharePicker', ['selectedMembers', 'selectedGroups']),
    ...mapGetters('room', ['isCurrentUserPm']),
    shareType () {
      if (this.shareTypeValue) {
        return this.shareTypeValue
      }
      return this.question.rights.isPublic
        ? SHARE_TYPE_OPTIONS.PUBLIC
        : SHARE_TYPE_OPTIONS.CUSTOM

    },
    shareTypeOptions () {
      return [
        {
          name: this.$t('project.question-answer.QuestionAnswerItemSharePane.customTitle'),
          value: SHARE_TYPE_OPTIONS.CUSTOM,
        },
      ]
    },
    notifyMessageLength () {
      return escapeHtml(this.customMessage).length
    },
    notifyMessageErrors () {
      const errors = []
      this.notifyMessageLength > NOTIFY_MESSAGE_MAX_LENGTH && errors.push(
        this.$t('common.validations.maxLength', {
          fieldName: this.$t('project.question-answer.QuestionAnswerItemSharePane.customMessage'),
          maxLength: NOTIFY_MESSAGE_MAX_LENGTH,
        }),
      )
      return errors
    },
  },
  watch: {
    modelValue (value) {
      if (value) {
        this.initSelectedGroupsAndMembers()
      }
    },
  },
  created () {
    if (this.shareType === SHARE_TYPE_OPTIONS.CUSTOM) {
      this.initSelectedGroupsAndMembers()
    }
  },
  methods: {
    ...mapActions('questionAnswer', [
      QuestionAnswerActionTypes.PATCH_QUESTION_RIGHTS,
      QuestionAnswerActionTypes.GET_QUESTION,
    ]),
    ...mapActions('documentSharePicker', [
      DocumentSharePickerActionTypes.RESET_SELECTED_GROUPS_AND_MEMBERS,
      DocumentSharePickerActionTypes.INIT_SELECTED_MEMBERS,
      DocumentSharePickerActionTypes.INIT_SELECTED_GROUPS,
    ]),
    closePane () {
      this.resetData()
      this.$emit('update:modelValue', false)
    },
    resetData () {
      this.shareTypeValue = ''
      this.customMessage = ''
      this.$refs.customMessageEditor?.clear()
      this.RESET_SELECTED_GROUPS_AND_MEMBERS()
    },
    onShareTypeChange(value) {
      this.shareTypeValue = value
      if (value === SHARE_TYPE_OPTIONS.CUSTOM) {
        this.initSelectedGroupsAndMembers()
      }
    },
    async preparePatchQuestionRights () {
      try {
        const data = {
          shareType: this.shareType,
          withUsers: this.selectedMembers.map(m => m.id),
          withGroups: this.selectedGroups.map(g => g.id),
        }
        if (this.customMessage) {
          data.customMessage = this.customMessage
        }
        await this.PATCH_QUESTION_RIGHTS({
          mnemo: this.mnemo,
          questionId: this.question.id,
          data,
        })
        toast.success(this.$t('project.question-answer.QuestionAnswerItemSharePane.patchQuestionRightsSuccess'))
        this.closePane()
        this.prepareGetQuestion()
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.question-answer.QuestionAnswerItemSharePane.patchQuestionRightsError'))
      }
    },
    initSelectedGroupsAndMembers () {
      this.INIT_SELECTED_GROUPS(this.question.rights.groups)
      this.INIT_SELECTED_MEMBERS(this.question.rights.users)
    },
    async prepareGetQuestion () {
      try {
        await this.GET_QUESTION({
          mnemo: this.mnemo,
          questionId: this.question.id,
        })
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.question-answer.QuestionAnswerItemSharePane.getQuestionError'))
        this.$router.push({ name: QUESTION_ANSWER_LIST_ROUTE_NAME })
      }
    },
  },
}
</script>

<style scoped lang="scss">
.DocumentShare-radio {
  border-left: 7px solid transparent;
  margin-left: -20px;
  margin-right: -20px;
  padding-top: 14px;
  padding-bottom: 14px;

  &--active {
    border-left-color: rgb(var(--v-theme-primary));
    background-color: rgb(var(--v-theme-primary-lighten-5));
  }

  &:not(:last-child) {
    margin-right: -20px !important;
  }
}

:deep(.v-selection-control-group) {
  width: 100%;
}
:deep(.v-selection-control .v-label) {
  width: 100%;
}
</style>
